/**
 * Component 8&9: Before / After Status
 * Combined component for visual comparison of before and after states
 * Supports multiple image uploads for both states
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Camera as PhotoIcon, 
  PlusIcon, 
  TrashIcon, 
  UploadIcon,
  EyeIcon,
  XIcon,
  ArrowLeftRight as CompareIcon
} from 'lucide-react';
import type { KATABoardV2 } from '../../../types/kataBoardV2';
import { FileAttachmentService } from '../../../services/fileAttachment';
import { kataBoardV2Api } from '../../../services/kataBoardV2';
import { useKataBoardV2Store } from '../../../stores/kataBoardV2Store';
import { useKataBoardCanEdit } from '../../../contexts/KataBoardAccessContext';

interface Component08_09Props {
  kataBoard: KATABoardV2;
}

interface ImageFile {
  id?: number;
  file?: File;
  url: string;
  name: string;
  size?: number;
  isUploading?: boolean;
  isExisting?: boolean;
  tempId?: number; // Temporary ID for tracking uploads
}

const Component08_09_BeforeAfterStatus: React.FC<Component08_09Props> = ({ kataBoard }) => {
  const [beforeImages, setBeforeImages] = useState<ImageFile[]>([]);
  const [afterImages, setAfterImages] = useState<ImageFile[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const canEdit = useKataBoardCanEdit();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<ImageFile | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [beforeCostInput, setBeforeCostInput] = useState('');
  const [afterCostInput, setAfterCostInput] = useState('');
  const [searchParams] = useSearchParams();
  const currentComponent = parseInt(searchParams.get('component') || '1');

  const formatCostForInput = (value: string | null | undefined): string => {
    if (value === null || value === undefined || value === '') return '';
    return String(value);
  };

  // Load existing images on component mount, when kataBoard.id changes,
  // or when navigating to this component (component 7)
  useEffect(() => {
    if (kataBoard.id && currentComponent === 7) {
      // Always reload when component becomes visible (component 7)
      // or when kataBoard.id changes
      loadExistingImages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kataBoard.id, currentComponent]);

  useEffect(() => {
    setBeforeCostInput(formatCostForInput(kataBoard.before_status_cost));
    setAfterCostInput(formatCostForInput(kataBoard.after_status_cost));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kataBoard.before_status_cost, kataBoard.after_status_cost]);

  const loadExistingImages = async () => {
    try {
      setLoading(true);
      
      // Fetch before images directly from API
      const beforeAttachments = await FileAttachmentService.getBeforeImages(kataBoard.id!);
      
      // Fetch after images directly from API
      const afterAttachments = await FileAttachmentService.getAfterImages(kataBoard.id!);

      setBeforeImages(beforeAttachments.map(attachment => ({
        id: attachment.id,
        url: attachment.file_url || attachment.file,
        name: attachment.original_filename,
        size: attachment.file_size,
        isExisting: true
      })));

      setAfterImages(afterAttachments.map(attachment => ({
        id: attachment.id,
        url: attachment.file_url || attachment.file,
        name: attachment.original_filename,
        size: attachment.file_size,
        isExisting: true
      })));

    } catch (err) {
      console.error('Failed to load existing images:', err);
      setError('Failed to load existing images');
      // Set empty arrays on error to prevent further errors
      setBeforeImages([]);
      setAfterImages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (files: FileList | null, type: 'before' | 'after') => {
    if (!files) return;

    const newImages: ImageFile[] = [];
    
    Array.from(files).forEach(file => {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError(`${file.name} is not a valid image file`);
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError(`${file.name} is too large. Maximum size is 10MB`);
        return;
      }

      const imageFile: ImageFile = {
        file,
        url: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
        isUploading: false,
        isExisting: false,
        tempId: Date.now() + Math.random() // Add unique identifier for tracking
      };

      newImages.push(imageFile);
    });

    if (type === 'before') {
      setBeforeImages(prev => [...prev, ...newImages]);
    } else {
      setAfterImages(prev => [...prev, ...newImages]);
    }
  };


  const removeImage = async (imageFile: ImageFile, type: 'before' | 'after') => {
    try {
      if (imageFile.id && imageFile.isExisting) {
        await FileAttachmentService.delete(imageFile.id);
      }

      // Clean up object URL if it's a local file
      if (!imageFile.isExisting && imageFile.url.startsWith('blob:')) {
        URL.revokeObjectURL(imageFile.url);
      }

      const updateImages = type === 'before' ? setBeforeImages : setAfterImages;
      updateImages(prev => prev.filter(img => img !== imageFile));

    } catch (err) {
      console.error('Failed to remove image:', err);
      setError(`Failed to remove ${imageFile.name}`);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Upload any pending images
      const beforeUploads = beforeImages.filter(img => !img.isExisting && img.file);
      const afterUploads = afterImages.filter(img => !img.isExisting && img.file);

      // Set uploading state for all pending images
      setBeforeImages(prev => prev.map(img => 
        beforeUploads.some(upload => upload.tempId === img.tempId) ? { ...img, isUploading: true } : img
      ));
      setAfterImages(prev => prev.map(img => 
        afterUploads.some(upload => upload.tempId === img.tempId) ? { ...img, isUploading: true } : img
      ));

      // Upload all images and collect results
      const uploadPromises = [
        ...beforeUploads.map(async (img) => {
          const attachmentType = 'before_image';
          const uploadedFile = await FileAttachmentService.create({
            kata_board: kataBoard.id,
            attachment_type: attachmentType,
            file: img.file!,
            title: 'Before Status Image',
            description: 'Before status documentation',
            is_before_image: true,
            is_after_image: false
          });
          return { originalImg: img, uploadedFile, type: 'before' as const };
        }),
        ...afterUploads.map(async (img) => {
          const attachmentType = 'after_image';
          const uploadedFile = await FileAttachmentService.create({
            kata_board: kataBoard.id,
            attachment_type: attachmentType,
            file: img.file!,
            title: 'After Status Image',
            description: 'After status documentation',
            is_before_image: false,
            is_after_image: true
          });
          return { originalImg: img, uploadedFile, type: 'after' as const };
        })
      ];

      await Promise.all(uploadPromises);

      // Save cost fields as raw text (with currency examples allowed).
      const normalizedBeforeCost = beforeCostInput.trim() ? beforeCostInput.trim() : null;
      const normalizedAfterCost = afterCostInput.trim() ? afterCostInput.trim() : null;

      const updatedBoard = await kataBoardV2Api.update(kataBoard.id!, {
        before_status_cost: normalizedBeforeCost,
        after_status_cost: normalizedAfterCost,
      });
      // Keep global board state in sync so tab switching doesn't show stale values.
      useKataBoardV2Store.setState((state) => ({
        currentKataBoard:
          state.currentKataBoard?.id === updatedBoard.id
            ? updatedBoard
            : state.currentKataBoard,
        kataBoards: state.kataBoards.map((kb) => (kb.id === updatedBoard.id ? updatedBoard : kb)),
      }));

      // Reload all images from API to ensure we have the latest data
      await loadExistingImages();

      setIsEditing(false);
    } catch (err: any) {
      console.error('Failed to save images:', err);
      setError(err?.message || 'Failed to save images');
      
      // Reset uploading state on error
      setBeforeImages(prev => prev.map(img => ({ ...img, isUploading: false })));
      setAfterImages(prev => prev.map(img => ({ ...img, isUploading: false })));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Clean up any object URLs for unsaved images
    [...beforeImages, ...afterImages].forEach(img => {
      if (!img.isExisting && img.url.startsWith('blob:')) {
        URL.revokeObjectURL(img.url);
      }
    });

    // Reload existing images
    loadExistingImages();
    setBeforeCostInput(formatCostForInput(kataBoard.before_status_cost));
    setAfterCostInput(formatCostForInput(kataBoard.after_status_cost));
    setIsEditing(false);
    setError(null);
  };

  const openImageModal = (image: ImageFile) => {
    setSelectedImage(image);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setShowImageModal(false);
  };

  const openComparison = () => {
    setShowComparison(true);
  };

  const closeComparison = () => {
    setShowComparison(false);
  };

  const renderImageGrid = (images: ImageFile[], type: 'before' | 'after') => {
    const title = type === 'before' ? 'Before Status' : 'After Status';
    const bgColor = type === 'before' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200';
    const headerColor = type === 'before' ? 'bg-red-500' : 'bg-green-500';

    return (
      <div className={`border-2 rounded-lg overflow-hidden ${bgColor}`}>
        {/* Header */}
        <div className={`${headerColor} text-white p-4 flex items-center justify-between`}>
          <h3 className="text-xl font-bold">{title}</h3>
          <div className="text-white opacity-80">
            {type === 'before' ? '📷' : '✨'}
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {images.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image, index) => (
                <div key={index} className="relative group">
                  <div className="aspect-square bg-white rounded-lg overflow-hidden shadow-sm border">
                    <img
                      src={image.url}
                      alt={`${title} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 flex space-x-2">
                        <button
                          onClick={() => openImageModal(image)}
                          className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100"
                          title="View Image"
                        >
                          <EyeIcon className="h-4 w-4 text-gray-600" />
                        </button>
                        {!isEditing && (
                          <button
                            onClick={() => openComparison()}
                            className="p-2 bg-white rounded-full shadow-lg hover:bg-blue-50"
                            title="Compare with other images"
                          >
                            <CompareIcon className="h-4 w-4 text-blue-600" />
                          </button>
                        )}
                        {isEditing && (
                          <button
                            onClick={() => removeImage(image, type)}
                            className="p-2 bg-white rounded-full shadow-lg hover:bg-red-50"
                            title="Delete Image"
                          >
                            <TrashIcon className="h-4 w-4 text-red-600" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Upload progress */}
                    {image.isUploading && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="text-white text-sm">Uploading...</div>
                      </div>
                    )}
                  </div>
                  
                  {/* Image info */}
                  <div className="mt-2 text-xs text-gray-600 truncate">
                    {image.name}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <PhotoIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No {type} images uploaded yet</p>
              {isEditing && (
                <p className="text-sm mt-2">Click "Add Images" to upload photos</p>
              )}
            </div>
          )}

          {/* Add Images Button */}
          {isEditing && (
            <div className="mt-4">
              <label className={`inline-flex items-center px-4 py-2 ${headerColor} text-white text-sm font-medium rounded-md hover:opacity-90 cursor-pointer`}>
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Images
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files, type)}
                />
              </label>
              <p className="text-xs text-gray-500 mt-2">
                Supported formats: JPG, PNG, GIF. Max size: 10MB per image.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Before / After Status</h3>
          <p className="text-sm text-gray-600 mt-1">
            Visual documentation of the current state and improvements achieved
          </p>
        </div>
        
        {!isEditing ? (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => openComparison()}
              disabled={beforeImages.length === 0 && afterImages.length === 0}
              className="kata-readonly-allowed inline-flex items-center px-3 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CompareIcon className="h-4 w-4 mr-2" />
              Compare Images
            </button>
            {canEdit && (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <PhotoIcon className="h-4 w-4 mr-2" />
              Manage Images
            </button>
            )}
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCancel}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <XIcon className="h-4 w-4 mr-2" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              <UploadIcon className="h-4 w-4 mr-2" />
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
          <button 
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Before/After Grid */}
      <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Cost Inputs</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cost Before
            </label>
            <input
              type="text"
              value={beforeCostInput}
              onChange={(e) => setBeforeCostInput(e.target.value)}
              disabled={!isEditing}
              placeholder="e.g. 12,500 EUR"
              className={`w-full px-3 py-2 border rounded-md text-sm ${
                isEditing
                  ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  : 'border-gray-200 bg-gray-100 text-gray-600'
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cost After
            </label>
            <input
              type="text"
              value={afterCostInput}
              onChange={(e) => setAfterCostInput(e.target.value)}
              disabled={!isEditing}
              placeholder="e.g. 9,800 EUR"
              className={`w-full px-3 py-2 border rounded-md text-sm ${
                isEditing
                  ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  : 'border-gray-200 bg-gray-100 text-gray-600'
              }`}
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          You can type currency text (example: "12,500 EUR"). The numeric value is saved.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {renderImageGrid(beforeImages, 'before')}
        {renderImageGrid(afterImages, 'after')}
      </div>

      {/* Statistics */}
      {(beforeImages.length > 0 || afterImages.length > 0) && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Documentation Summary</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Before Images:</span>
              <span className="ml-2 font-medium">{beforeImages.length}</span>
            </div>
            <div>
              <span className="text-gray-600">After Images:</span>
              <span className="ml-2 font-medium">{afterImages.length}</span>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4"
          onClick={closeImageModal}
        >
          {/* Close Button - Fixed Position */}
          <button
            onClick={closeImageModal}
            className="kata-readonly-allowed fixed top-4 right-4 z-[70] bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full p-2 transition-all duration-200"
            title="Close Image"
          >
            <XIcon className="h-6 w-6" />
          </button>
          
          <div 
            className="relative max-w-4xl max-h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.url}
              alt={selectedImage.name}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-4 rounded-b-lg">
              <p className="text-sm font-medium">{selectedImage.name}</p>
              {selectedImage.size && (
                <p className="text-xs opacity-75">
                  {(selectedImage.size / 1024 / 1024).toFixed(2)} MB
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comparison Modal */}
      {showComparison && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-6xl h-full max-h-full bg-white rounded-lg overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <h3 className="text-lg font-medium text-gray-900">Before / After Comparison</h3>
              <button
                onClick={closeComparison}
                className="kata-readonly-allowed text-gray-400 hover:text-gray-600"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="h-full overflow-y-auto">
              {/* Before Section */}
              <div className="bg-red-50 border-b-4 border-red-500">
                {/* Before Header */}
                <div className="bg-red-500 text-white p-4">
                  <h4 className="text-xl font-bold text-center">📷 Before Status</h4>
                  <p className="text-center text-red-100 text-sm mt-1">
                    {beforeImages.length} image{beforeImages.length !== 1 ? 's' : ''} available
                  </p>
                </div>

                {/* Before Images Grid */}
                <div className="p-6">
                  {beforeImages.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {beforeImages.map((image, index) => (
                        <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden border-2 border-red-200 hover:border-red-400 transition-all">
                          <div className="aspect-video relative group">
                            <img
                              src={image.url}
                              alt={`Before ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                            
                            {/* Hover Overlay */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 flex space-x-2">
                                <button
                                  onClick={() => openImageModal(image)}
                                  className="kata-readonly-allowed p-2 bg-white rounded-full shadow-lg hover:bg-gray-100"
                                  title="View Full Size"
                                >
                                  <EyeIcon className="h-4 w-4 text-gray-600" />
                                </button>
                              </div>
                            </div>
                          </div>
                          
                          {/* Image Info */}
                          <div className="p-3 bg-red-50">
                            <p className="text-sm font-medium text-red-800 truncate">{image.name}</p>
                            {image.size && (
                              <p className="text-xs text-red-600 mt-1">
                                {(image.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-red-600">
                      <PhotoIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium">No Before Images</p>
                      <p className="text-sm mt-2">Upload some before images to see them here</p>
                    </div>
                  )}
                </div>
              </div>

              {/* After Section */}
              <div className="bg-green-50">
                {/* After Header */}
                <div className="bg-green-500 text-white p-4">
                  <h4 className="text-xl font-bold text-center">✨ After Status</h4>
                  <p className="text-center text-green-100 text-sm mt-1">
                    {afterImages.length} image{afterImages.length !== 1 ? 's' : ''} available
                  </p>
                </div>

                {/* After Images Grid */}
                <div className="p-6">
                  {afterImages.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {afterImages.map((image, index) => (
                        <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden border-2 border-green-200 hover:border-green-400 transition-all">
                          <div className="aspect-video relative group">
                            <img
                              src={image.url}
                              alt={`After ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                            
                            {/* Hover Overlay */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 flex space-x-2">
                                <button
                                  onClick={() => openImageModal(image)}
                                  className="kata-readonly-allowed p-2 bg-white rounded-full shadow-lg hover:bg-gray-100"
                                  title="View Full Size"
                                >
                                  <EyeIcon className="h-4 w-4 text-gray-600" />
                                </button>
                              </div>
                            </div>
                          </div>
                          
                          {/* Image Info */}
                          <div className="p-3 bg-green-50">
                            <p className="text-sm font-medium text-green-800 truncate">{image.name}</p>
                            {image.size && (
                              <p className="text-xs text-green-600 mt-1">
                                {(image.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-green-600">
                      <PhotoIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium">No After Images</p>
                      <p className="text-sm mt-2">Upload some after images to see them here</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Component08_09_BeforeAfterStatus;
